import { types, Instance } from "mobx-state-tree";
import { NumberModel } from "../../mobxDataModels/numberModel";
import { StringModel } from "../../mobxDataModels/stringModel";
import { defaultValues } from "../base";

export const VolumeSetDraftAutoscalingModel = types
  .model({
    _maxCapacity: types.optional(types.number, defaultValues.volumeset.autoscaling.maxCapacity.default),
    _minFreePercentage: types.optional(types.number, defaultValues.volumeset.autoscaling.minFreePercentage.default),
    _scalingFactor: types.optional(types.number, defaultValues.volumeset.autoscaling.scalingFactor.default),
    _enablePredictive: types.optional(types.boolean, false),
    _lookbackHours: types.optional(types.number, defaultValues.volumeset.autoscaling.predictive.lookbackHours.default),
    _projectionHours: types.optional(types.number, defaultValues.volumeset.autoscaling.predictive.projectionHours.default),
    _minDataPoints: types.optional(types.number, defaultValues.volumeset.autoscaling.predictive.minDataPoints.default),
    _minGrowthRateGBPerHour: types.optional(types.number, defaultValues.volumeset.autoscaling.predictive.minGrowthRateGBPerHour.default),
    _predictiveScalingFactor: types.maybe(types.number),
    maxCapacity: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Max Capacity",
        min: defaultValues.volumeset.autoscaling.maxCapacity.min,
        initialValue: defaultValues.volumeset.autoscaling.maxCapacity.default,
      })
    ),
    minFreePercentage: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Min Free Percentage",
        min: defaultValues.volumeset.autoscaling.minFreePercentage.min,
        max: defaultValues.volumeset.autoscaling.minFreePercentage.max,
        initialValue: defaultValues.volumeset.autoscaling.minFreePercentage.default,
      })
    ),
    scalingFactor: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Scaling Factor",
        allowDot: true,
        min: defaultValues.volumeset.autoscaling.scalingFactor.min,
        initialValue: defaultValues.volumeset.autoscaling.scalingFactor.default,
      })
    ),
    enablePredictive: types.optional(types.boolean, false),
    lookbackHours: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Lookback Hours",
        min: defaultValues.volumeset.autoscaling.predictive.lookbackHours.min,
        max: defaultValues.volumeset.autoscaling.predictive.lookbackHours.max,
        initialValue: defaultValues.volumeset.autoscaling.predictive.lookbackHours.default,
      })
    ),
    projectionHours: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Projection Hours",
        min: defaultValues.volumeset.autoscaling.predictive.projectionHours.min,
        max: defaultValues.volumeset.autoscaling.predictive.projectionHours.max,
        initialValue: defaultValues.volumeset.autoscaling.predictive.projectionHours.default,
      })
    ),
    minDataPoints: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Min Data Points",
        min: defaultValues.volumeset.autoscaling.predictive.minDataPoints.min,
        max: defaultValues.volumeset.autoscaling.predictive.minDataPoints.max,
        initialValue: defaultValues.volumeset.autoscaling.predictive.minDataPoints.default,
      })
    ),
    minGrowthRateGBPerHour: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Min Growth Rate (GB/Hour)",
        allowDot: true,
        min: defaultValues.volumeset.autoscaling.predictive.minGrowthRateGBPerHour.min,
        initialValue: defaultValues.volumeset.autoscaling.predictive.minGrowthRateGBPerHour.default,
      })
    ),
    predictiveScalingFactor: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Predictive Scaling Factor",
        allowDot: true,
        min: defaultValues.volumeset.autoscaling.predictive.scalingFactor.min,
        initialValue: defaultValues.volumeset.autoscaling.predictive.scalingFactor.default || 0,
      })
    ),
  })
  .actions((self) => ({
    reset() {
      self.maxCapacity.setInitialValue(String(self._maxCapacity));
      self.minFreePercentage.setInitialValue(String(self._minFreePercentage));
      self.scalingFactor.setInitialValue(String(self._scalingFactor));
      self.enablePredictive = self._enablePredictive;
      self.lookbackHours.setInitialValue(String(self._lookbackHours));
      self.projectionHours.setInitialValue(String(self._projectionHours));
      self.minDataPoints.setInitialValue(String(self._minDataPoints));
      self.minGrowthRateGBPerHour.setInitialValue(String(self._minGrowthRateGBPerHour));
      self.predictiveScalingFactor.setInitialValue(
        self._predictiveScalingFactor !== undefined && self._predictiveScalingFactor !== null
          ? String(self._predictiveScalingFactor)
          : ""
      );
    },
    setEnablePredictive(value: boolean) {
      self.enablePredictive = value;
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();
    },
    confirm() {
      self._maxCapacity = Number(self.maxCapacity.value);
      self._minFreePercentage = Number(self.minFreePercentage.value);
      self._scalingFactor = Number(self.scalingFactor.value);
      self._enablePredictive = self.enablePredictive;
      self._lookbackHours = Number(self.lookbackHours.value);
      self._projectionHours = Number(self.projectionHours.value);
      self._minDataPoints = Number(self.minDataPoints.value);
      self._minGrowthRateGBPerHour = Number(self.minGrowthRateGBPerHour.value);
      self._predictiveScalingFactor = self.predictiveScalingFactor.value
        ? Number(self.predictiveScalingFactor.value)
        : undefined;
    },
  }))
  .views((self) => ({
    get isDirty() {
      let res = false;
      if (self._maxCapacity !== Number(self.maxCapacity.value)) {
        res = true;
      }
      if (self._minFreePercentage !== Number(self.minFreePercentage.value)) {
        res = true;
      }
      if (self._scalingFactor !== Number(self.scalingFactor.value)) {
        res = true;
      }
      if (self._enablePredictive !== self.enablePredictive) {
        res = true;
      }
      if (self.enablePredictive) {
        if (self._lookbackHours !== Number(self.lookbackHours.value)) {
          res = true;
        }
        if (self._projectionHours !== Number(self.projectionHours.value)) {
          res = true;
        }
        if (self._minDataPoints !== Number(self.minDataPoints.value)) {
          res = true;
        }
        if (self._minGrowthRateGBPerHour !== Number(self.minGrowthRateGBPerHour.value)) {
          res = true;
        }
        const currentPredScalingFactor = self.predictiveScalingFactor.value
          ? Number(self.predictiveScalingFactor.value)
          : undefined;
        if (self._predictiveScalingFactor !== currentPredScalingFactor) {
          res = true;
        }
      }
      return res;
    },
    get isValid() {
      let res = true;
      if (!self.maxCapacity.isValid) {
        res = false;
      }
      if (!self.minFreePercentage.isValid) {
        res = false;
      }
      if (!self.scalingFactor.isValid) {
        res = false;
      }
      if (self.enablePredictive) {
        if (!self.lookbackHours.isValid) {
          res = false;
        }
        if (!self.projectionHours.isValid) {
          res = false;
        }
        if (!self.minDataPoints.isValid) {
          res = false;
        }
        if (!self.minGrowthRateGBPerHour.isValid) {
          res = false;
        }
      }
      return res;
    },
    get asObject() {
      const obj: any = {
        maxCapacity: Number(self.maxCapacity.value),
        minFreePercentage: Number(self.minFreePercentage.value),
        scalingFactor: Number(self.scalingFactor.value),
      };
      if (self.enablePredictive) {
        obj.predictive = {
          enabled: true,
          lookbackHours: Number(self.lookbackHours.value),
          projectionHours: Number(self.projectionHours.value),
          minDataPoints: Number(self.minDataPoints.value),
          minGrowthRateGBPerHour: Number(self.minGrowthRateGBPerHour.value),
        };
        if (self.predictiveScalingFactor.value) {
          obj.predictive.scalingFactor = Number(self.predictiveScalingFactor.value);
        }
      }
      return obj;
    },
  }));
export interface VolumeSetDraftAutoscalingMobx extends Instance<typeof VolumeSetDraftAutoscalingModel> {}
